import React from 'react';
import { Button, Empty, Popconfirm, Tag, message } from 'antd';
import api from '../../../lib/api';
import formatDuration from '../../../utils/formatDuration';
import type { TimeEntry } from '../../../types/timeEntry';

type TimeEntryListProps = {
  entries: TimeEntry[];
  onEdit: (entry: TimeEntry) => void;
  onDeleted: () => void;
};

function formatTimeRange(entry: TimeEntry): string {
  const start = new Date(entry.started_at);
  const label = (date: Date) =>
    `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

  if (!entry.ended_at) return `${label(start)} — 进行中`;
  return `${label(start)} — ${label(new Date(entry.ended_at))}`;
}

const TimeEntryList: React.FC<TimeEntryListProps> = ({ entries, onEdit, onDeleted }) => {
  const remove = async (entry: TimeEntry) => {
    try {
      await api.timeEntries.destroy(entry.id);
      onDeleted();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '删除失败');
    }
  };

  if (entries.length === 0) {
    return <Empty description="这段时间还没有计时记录" className="py-8" />;
  }

  return (
    <div className="divide-y divide-gray-100">
      {entries.map((entry) => (
        <div key={entry.id} className="flex items-center gap-3 py-2.5 text-sm">
          <span
            className="h-2 w-2 flex-none rounded-full"
            style={{ background: entry.project_color ?? '#d9d9d9' }}
          />

          <div className="min-w-0 flex-1">
            <div className="truncate text-gray-900">
              {entry.task_title ?? entry.project_name ?? '未归类计时（无任务）'}
            </div>
            <div className="text-xs text-gray-500">
              {formatTimeRange(entry)}
              {entry.note && <span className="ml-2 text-gray-400">{entry.note}</span>}
            </div>
          </div>

          {entry.source === 'manual' && (
            <Tag bordered={false} className="!mr-0">
              补录
            </Tag>
          )}
          {entry.suspicious && (
            <Tag bordered={false} color="gold" className="!mr-0">
              可疑
            </Tag>
          )}

          <span className="flex-none tabular-nums text-gray-700">
            {formatDuration(Math.round((entry.duration_seconds ?? 0) / 60))}
          </span>

          <Button size="small" type="text" onClick={() => onEdit(entry)}>
            编辑
          </Button>
          <Popconfirm
            title="删除这条计时记录？"
            okText="删除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
            onConfirm={() => remove(entry)}
          >
            <Button size="small" type="text" danger>
              删除
            </Button>
          </Popconfirm>
        </div>
      ))}
    </div>
  );
};

export default TimeEntryList;
